/**
 * Calendar Management Screen Component
 *
 * Full-screen modal for managing calendar and label visibility preferences.
 */

import { useEffect } from 'react';
import { Modal, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';

import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getHeadingFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import type { FamilyMember } from '@/types/family-member';
import { CalendarSelector } from './CalendarSelector';

type CalendarManagementScreenProps = {
  visible: boolean;
  onClose: () => void;
  calendars: DeviceCalendar[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  /** Family members for label filtering */
  familyMembers?: FamilyMember[];
  /** Currently selected family label IDs */
  selectedLabelIds?: string[];
  /** Callback when label selection changes */
  onLabelChange?: (labelIds: string[]) => void;
};

const DRAWER_WIDTH = 320;
const ANIMATION_DURATION = 250;

export function CalendarManagementScreen({
  visible,
  onClose,
  calendars,
  selectedIds,
  onChange,
  familyMembers = [],
  selectedLabelIds = [],
  onLabelChange,
}: CalendarManagementScreenProps) {
  const colorScheme = useColorScheme();
  const { top: topInset, bottom: bottomInset } = useSafeAreaInsets();
  const palette = Colors[colorScheme ?? 'light'];

  const textColor = palette.text;
  const backgroundColor = palette.background;
  const borderColor = palette.border;

  // Animation values
  const translateX = useSharedValue(-DRAWER_WIDTH);
  const backdropOpacity = useSharedValue(0);

  // Animate drawer and backdrop when visibility changes
  useEffect(() => {
    if (visible) {
      translateX.value = withTiming(0, { duration: ANIMATION_DURATION });
      backdropOpacity.value = withTiming(1, { duration: ANIMATION_DURATION });
    } else {
      translateX.value = withTiming(-DRAWER_WIDTH, { duration: ANIMATION_DURATION });
      backdropOpacity.value = withTiming(0, { duration: ANIMATION_DURATION });
    }
  }, [visible, translateX, backdropOpacity]);

  // Animated styles
  const drawerAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  const backdropAnimatedStyle = useAnimatedStyle(() => ({
    opacity: backdropOpacity.value,
  }));

  // Show labels if family members and handler are provided
  const showLabels = familyMembers.length > 0 && !!onLabelChange;
  const headerTitle = showLabels ? 'Filter Calendars and Labels' : 'Filter Calendars';

  if (!visible) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={styles.container}>
        {/* Backdrop */}
        <Animated.View
          style={[styles.backdrop, backdropAnimatedStyle]}
        >
          <TouchableOpacity
            style={StyleSheet.absoluteFillObject}
            activeOpacity={1}
            onPress={onClose}
          />
        </Animated.View>

        {/* Drawer */}
        <Animated.View
          style={[
            styles.drawer,
            {
              backgroundColor,
              borderRightColor: borderColor,
              paddingTop: topInset,
              paddingBottom: bottomInset,
            },
            drawerAnimatedStyle,
          ]}
        >
          <View style={[styles.header, { borderBottomColor: borderColor }]}>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={textColor} />
            </TouchableOpacity>
            <Text style={[styles.headerTitle, { color: textColor }]} numberOfLines={1}>
              {headerTitle}
            </Text>
            <View style={styles.rightHeaderPlaceholder} />
          </View>

          <View style={styles.content}>
            <CalendarSelector
              calendars={calendars}
              selectedIds={selectedIds}
              onChange={onChange}
              familyMembers={familyMembers}
              selectedLabelIds={selectedLabelIds}
              onLabelChange={onLabelChange}
              showLabels={showLabels}
            />
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  drawer: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: DRAWER_WIDTH,
    borderRightWidth: 1,
    shadowColor: '#000',
    shadowOpacity: 0.3,
    shadowOffset: { width: 2, height: 0 },
    shadowRadius: 8,
    elevation: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderBottomWidth: 1,
  },
  closeButton: {
    padding: 4,
  },
  headerTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    fontFamily: getHeadingFont(),
    textAlign: 'center',
    marginHorizontal: Spacing.sm,
  },
  rightHeaderPlaceholder: {
    width: 32,
  },
  content: {
    flex: 1,
  },
});
